import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import PagesTab from './PagesTab';
import EventsTab from './EventsTab';

const SessionDetailView = ({ session }) => {
  const [copiedItems, setCopiedItems] = useState(new Set());
  const [activeTab, setActiveTab] = useState('properties');

  if (!session) {
    return (
      <div className="px-6 py-8 text-center text-sm text-[#475569]" style={{ fontFamily: 'Raleway, sans-serif' }}>
        No session data available.
      </div>
    );
  }

  // Match TransactionDetailView / TransactionPropertiesTab: grey, rounded container
  const tabButtons = [
    { id: 'properties', label: 'Properties' },
    { id: 'pages', label: 'Pages' },
    { id: 'events', label: 'Events' }
  ];

  const formatValue = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') {
      if (Array.isArray(value)) return value.length > 0 ? JSON.stringify(value, null, 2) : null;
      return Object.keys(value).length > 0 ? JSON.stringify(value, null, 2) : null;
    }
    const s = String(value).trim();
    return s.length > 0 ? s : null;
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(typeof text === 'object' ? JSON.stringify(text, null, 2) : String(text));
      setCopiedItems(prev => new Set([...prev, label]));
      setTimeout(() => setCopiedItems(prev => { const n = new Set(prev); n.delete(label); return n; }), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatDate = (v) => (v ? new Date(v).toLocaleString() : null);
  const formatDuration = (s) => {
    if (s == null) return null;
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
    return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
  };

  // All session fields from backend (transformSessionRow) + common display names
  const allProperties = [
    { label: 'Session ID', value: session.sessionId, copyable: true },
    { label: 'Distinct ID', value: session.distinctId, copyable: true },
    { label: 'User ID', value: session.userId, copyable: true },
    { label: 'Site ID', value: session.siteId, copyable: true },
    { label: 'Team ID', value: session.teamId, copyable: true },
    { label: 'Start Time', value: formatDate(session.startTime) },
    { label: 'End Time', value: formatDate(session.endTime) },
    { label: 'Last Activity', value: formatDate(session.lastActivity) },
    { label: 'Duration', value: formatDuration(session.duration) },
    { label: 'Pages Viewed', value: session.pagesViewed },
    { label: 'Is Bounce', value: session.isBounce !== undefined ? (session.isBounce ? 'Yes' : 'No') : null },
    { label: 'Previous Session ID', value: session.previousSessionId, copyable: true },
    { label: 'Time Since Last Session', value: session.timeSinceLastSession != null ? formatDuration(session.timeSinceLastSession) : null },
    { label: 'Entry Page', value: session.entryPage || session.entry_page, copyable: true },
    { label: 'Exit Page', value: session.exitPage || session.exit_page, copyable: true },
    { label: 'Referrer', value: session.referrer, copyable: true },
    { label: 'Is Web3 User', value: session.isWeb3User !== undefined ? (session.isWeb3User ? 'Yes' : 'No') : null },
    { label: 'Wallet Connected', value: session.walletConnected !== undefined ? (session.walletConnected ? 'Yes' : 'No') : null },
    { label: 'Country', value: session.country || session.locationData?.country },
    { label: 'IP Address', value: session.locationData?.ipAddress, copyable: true },
    { label: 'City', value: session.locationData?.city },
    { label: 'Region', value: session.locationData?.region },
    { label: 'Latitude', value: session.locationData?.latitude },
    { label: 'Longitude', value: session.locationData?.longitude },
    { label: 'Timezone', value: session.locationData?.timezone },
    { label: 'Organization', value: session.locationData?.org },
    { label: 'Postal Code', value: session.locationData?.postal },
    { label: 'Browser', value: session.browser?.name },
    { label: 'Browser Version', value: session.browser?.version },
    { label: 'Device Type', value: session.device?.type },
    { label: 'OS', value: session.device?.os },
    { label: 'Resolution', value: session.device?.resolution },
    { label: 'Language', value: session.device?.language },
    { label: 'User Agent', value: session.device?.userAgent, copyable: true },
    { label: 'Wallet Address', value: session.wallet?.walletAddress, copyable: true },
    { label: 'Wallet Type', value: session.wallet?.walletType },
    { label: 'Chain Name', value: session.wallet?.chainName },
    { label: 'UTM Source', value: session.utmData?.source },
    { label: 'UTM Medium', value: session.utmData?.medium },
    { label: 'UTM Campaign', value: session.utmData?.campaign },
    { label: 'UTM Term', value: session.utmData?.term },
    { label: 'UTM Content', value: session.utmData?.content },
    { label: 'UTM ID', value: session.utmData?.utm_id },
  ];

  const meaningfulProperties = allProperties
    .map(p => ({ ...p, value: formatValue(p.value) }))
    .filter(p => p.value !== null && p.value !== undefined && p.value !== '');

  // Same design as TransactionPropertiesTab: grey divide, hover, 3 per row
  const renderPropertiesTable = (properties) => {
    if (properties.length === 0) {
      return (
        <div className="px-6 py-8 text-center text-[#475569]">
          <p className="text-sm">No properties found.</p>
        </div>
      );
    }
    return (
      <div className="divide-y divide-gray-100">
        {Array.from({ length: Math.ceil(properties.length / 3) }).map((_, rowIndex) => {
          const rowProps = properties.slice(rowIndex * 3, rowIndex * 3 + 3);
          return (
            <div
              key={`row-${session.sessionId}-${rowIndex}`}
              className="px-6 py-2 hover:bg-gray-50 transition-colors grid grid-cols-3 gap-4 items-center"
            >
              {rowProps.map((prop, colIndex) => (
                <div key={`prop-${session.sessionId}-${rowIndex}-${colIndex}`} className="group flex items-center space-x-2 min-w-0">
                  <span
                    className="text-xs font-medium text-[#475569] flex-shrink-0"
                    style={{ fontFamily: 'Raleway, sans-serif' }}
                  >
                    {prop.label}
                  </span>
                  <span
                    className="text-sm text-[#0f172a] break-words flex-1 min-w-0"
                    style={{ fontFamily: 'Raleway, sans-serif' }}
                  >
                    {prop.value}
                  </span>
                  {prop.copyable && (
                    <button
                      onClick={() => copyToClipboard(prop.value, prop.label)}
                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 transition-all duration-200 flex-shrink-0"
                      title="Copy to clipboard"
                    >
                      {copiedItems.has(prop.label) ? (
                        <Check className="h-3 w-3 text-green-600" />
                      ) : (
                        <Copy className="h-3 w-3 text-[#475569]" />
                      )}
                    </button>
                  )}
                </div>
              ))}
              {rowProps.length < 3 && Array.from({ length: 3 - rowProps.length }).map((_, emptyIndex) => (
                <div key={`empty-${emptyIndex}`} />
              ))}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Round pill tabs, no icons - match transactions style */}
      <div className="border-b border-gray-200 bg-gray-50/50 px-6 py-3">
        <nav className="flex gap-2">
          {tabButtons.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-gray-200 text-[#0f172a]'
                  : 'bg-gray-100 text-[#475569] hover:bg-gray-200 hover:text-[#475569]'
              }`}
              style={{ fontFamily: 'Raleway, sans-serif' }}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="p-0">
        {activeTab === 'properties' && <div>{renderPropertiesTable(meaningfulProperties)}</div>}
        {activeTab === 'pages' && (
          <div className="p-6">
            <PagesTab session={session} />
          </div>
        )}
        {activeTab === 'events' && <EventsTab session={session} />}
      </div>
    </div>
  );
};

export default SessionDetailView;
